import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/apiClient';
import API_CONFIG from '../config/apiConfig';
import { CheckCircleIcon, ArrowTopRightOnSquareIcon, ClockIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import './LmsEnrollmentPending.css';

const LmsEnrollmentPending = () => {
  const { publicId } = useParams();
  const navigate = useNavigate();
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState('');
  const [lastCheckedAt, setLastCheckedAt] = useState(null);
  const launchInFlight = useRef(false);
  const loadingInFlight = useRef(false);

  const handleLaunchNow = useCallback(async () => {
    if (!publicId || launchInFlight.current) return;
    launchInFlight.current = true;
    setLaunching(true);
    try {
      const response = await api.post(API_CONFIG.lms.enrollmentLaunch(publicId));
      if (!response.data?.launch_url) throw new Error('No launch link returned');
      window.location.assign(response.data.launch_url);
    } catch (err) {
      setError(err.response?.data?.error || 'We could not connect to the LMS. Please try again shortly.');
      launchInFlight.current = false;
      setLaunching(false);
    }
  }, [publicId]);

  const statusLabel = useMemo(() => {
    if (!enrollment) return 'Preparing your learning space';
    switch (enrollment.status) {
      case 'provisioning': return 'Waiting for LMS confirmation';
      case 'pending_approval': return 'Awaiting enrolment approval';
      case 'rejected': return 'Enrolment not approved';
      case 'withdrawn': return 'Application withdrawn';
      case 'ready_to_launch': return 'Ready to launch';
      case 'active': return 'Learning is live';
      case 'completed': return 'Course completed';
      case 'certificate_awarded': return 'Certificate awarded';
      default: return enrollment.status_display || 'Pending';
    }
  }, [enrollment]);

  const loadEnrollment = useCallback(async () => {
    if (!publicId || loadingInFlight.current || launchInFlight.current) return;
    loadingInFlight.current = true;
    try {
      setError('');
      const response = await api.get(API_CONFIG.lms.enrollmentDetail(publicId));
      const data = response.data;
      setEnrollment(data);
      setLastCheckedAt(new Date().toISOString());

      if (data?.can_launch) {
        await handleLaunchNow();
      } else if (data?.last_error) {
        setError(data.last_error);
      }
    } catch (err) {
      console.error('Failed to load LMS enrollment status:', err);
      setError('We could not load the LMS enrollment status right now. We will keep trying.');
    } finally {
      setLoading(false);
      loadingInFlight.current = false;
    }
  }, [publicId, handleLaunchNow]);

  useEffect(() => {
    loadEnrollment();
    const timer = window.setInterval(loadEnrollment, 10000);
    return () => window.clearInterval(timer);
  }, [loadEnrollment]);

  if (loading && !enrollment) {
    return (
      <div className="lms-pending-page">
        <div className="lms-pending-shell">
          <div className="lms-pending-card lms-pending-card--centered">
            <div className="lms-pending-spinner" />
            <h1 className="lms-pending-title">Preparing your learning space</h1>
            <p className="lms-pending-text">We are handing your acceptance to the LMS and confirming your access.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lms-pending-page">
      <div className="lms-pending-shell">
        <div className="lms-pending-card">
          <div className="lms-pending-header">
            <div className="lms-pending-icon">
              <ClockIcon className="lms-pending-icon-svg" />
            </div>
            <div className="lms-pending-header-copy">
              <p className="lms-pending-eyebrow">LMS handoff</p>
              <h1 className="lms-pending-title">{statusLabel}</h1>
              <p className="lms-pending-text">
                {enrollment?.status === 'pending_approval'
                  ? 'Your application is with the LMS team. Campus training and paid courses require approval. You will receive an in-app notification when a decision is made.'
                  : enrollment?.status === 'rejected'
                    ? enrollment?.metadata?.decision_reason || 'Your application was not approved. Please contact learner support if you need more information.'
                    : enrollment?.status === 'withdrawn' ? 'This application has been withdrawn and no longer grants course access.'
                      : 'We are confirming your learner record and course access. Free online courses with a confirmed zero fee are approved automatically.'}
              </p>
            </div>
          </div>

          <div className="lms-pending-details">
            <div className="lms-pending-detail">
              <p className="lms-pending-detail-label">Course</p>
              <p className="lms-pending-detail-value">{enrollment?.course?.programme_name || 'Your selected programme'}</p>
            </div>
            <div className="lms-pending-detail">
              <p className="lms-pending-detail-label">Status</p>
              <p className="lms-pending-detail-value">{enrollment?.status_display || statusLabel}</p>
            </div>
            <div className="lms-pending-detail">
              <p className="lms-pending-detail-label">Last checked</p>
              <p className="lms-pending-detail-value">{lastCheckedAt ? new Date(lastCheckedAt).toLocaleTimeString('en-ZA') : 'Just now'}</p>
            </div>
          </div>

          {error && (
            <div className="lms-pending-alert">
              {error}
            </div>
          )}

          <div className="lms-pending-next">
            <div className="lms-pending-next-title">
              <CheckCircleIcon className="lms-pending-next-icon" />
              <span>What happens next</span>
            </div>
            <ul className="lms-pending-list">
              <li>The LMS confirms your learner record and, where required, a class place.</li>
              {enrollment?.status === 'pending_approval' && <li>A staff member reviews your application and you are notified of the outcome.</li>}
              <li>Your learning session is opened with a secure one-time token.</li>
              <li>Progress updates flow back to your careers dashboard automatically.</li>
            </ul>
          </div>

          <div className="lms-pending-actions">
            <button
              type="button"
              onClick={loadEnrollment}
              className="lms-button lms-button--secondary"
            >
              <ArrowPathIcon className="lms-button-icon" />
              Refresh status
            </button>
            <button
              type="button"
              disabled={!enrollment?.can_launch || launching}
              onClick={handleLaunchNow}
              className="lms-button lms-button--primary"
            >
              <ArrowTopRightOnSquareIcon className="lms-button-icon" />
              {launching ? 'Launching...' : 'Launch LMS'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="lms-button lms-button--ghost"
            >
              Back to dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LmsEnrollmentPending;
